"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import type { SelfSchedulePrefilledService } from "@/hooks/self-schedule/use-self-schedule";
import { cx } from "@/lib/tailwind-utils";

/** How long one chip takes to travel from its card to the selection counter. */
const FLIGHT_MS = 750;
/** Gap between chips when a link preselected more than one service. */
const STAGGER_MS = 200;
/** Ceiling on waiting for a smooth scroll to land before measuring anyway. */
const SCROLL_TIMEOUT_MS = 1500;
/** Frames of an unchanged card position that count as "the scroll has landed". */
const SCROLL_STABLE_FRAMES = 3;
/** Lets the counter mount and the list settle before measuring. */
const LAYOUT_SETTLE_MS = 80;
interface Flight extends SelfSchedulePrefilledService {
  originX: number;
  originY: number;
  deltaX: number;
  deltaY: number;
  delay: number;
}
const centerOf = (element: Element) => {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };
};

/**
 * Only one card can be scrolled to, so a link naming several services leaves the
 * rest wherever they happen to sit — sometimes thousands of pixels off-screen. A
 * chip launched from there spends most of its flight invisible and arrives from
 * nowhere, so those services skip the animation; the counter total and the live
 * announcement still cover them.
 */
const onScreenCenterOf = (element: Element) => {
  const center = centerOf(element);
  if (center.y < 0 || center.y > window.innerHeight) return null;
  return center;
};
const isFullyVisible = (element: Element) => {
  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.bottom <= window.innerHeight;
};

/**
 * Smooth scrolling reports no completion event we can rely on across browsers,
 * and measuring mid-scroll launches the chip from where the card *was* — which
 * puts most of the flight off-screen. Poll until the card stops moving instead.
 * Returns a canceller for the pending frame.
 */
const whenScrollSettles = (element: Element, done: () => void) => {
  let frame = 0;
  let lastTop = Number.NaN;
  let stableFrames = 0;
  const startedAt = performance.now();
  const check = () => {
    const {
      top
    } = element.getBoundingClientRect();
    stableFrames = Math.abs(top - lastTop) < 1 ? stableFrames + 1 : 0;
    lastTop = top;
    if (stableFrames >= SCROLL_STABLE_FRAMES || performance.now() - startedAt > SCROLL_TIMEOUT_MS) {
      done();
      return;
    }
    frame = requestAnimationFrame(check);
  };
  frame = requestAnimationFrame(check);
  return () => cancelAnimationFrame(frame);
};

/**
 * Pulses for `durationMs` every time `pulse()` fires. Paired with
 * `PreselectedServiceFlight` so the counter reacts as each chip lands.
 */
export function useArrivalPulse(t0) {
  const $ = _c(7);
  const durationMs = t0 === undefined ? 900 : t0;
  const [isPulsing, setIsPulsing] = useState(false);
  const timer = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== durationMs) {
    t3 = () => {
      setIsPulsing(true);
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => setIsPulsing(false), durationMs);
    };
    $[2] = durationMs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const pulse = t3;
  let t4;
  if ($[4] !== isPulsing || $[5] !== pulse) {
    t4 = {
      isPulsing,
      pulse
    };
    $[4] = isPulsing;
    $[5] = pulse;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
interface PreselectedServiceFlightProps {
  /** Services a `?serviceId=` link already dropped in the cart. */
  services: SelfSchedulePrefilledService[];
  /** Fires as each chip lands on the counter. */
  onArrive: () => void;
  /** Fires once the whole sequence is over, animated or skipped. */
  onFinished: () => void;
}

/**
 * A direct booking link (`?serviceId=`) fills the cart before the patient has
 * looked at anything, so the SERVICE step opens with a service silently
 * selected — often below the fold. This scrolls that card into view and flies
 * a chip from it down into the selection counter, borrowing the add-to-cart
 * gesture patients already know from shopping carts.
 */
export function PreselectedServiceFlight(t0) {
  const $ = _c(16);
  const {
    services,
    onArrive,
    onFinished
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [flights, setFlights] = useState(t1);
  const [isLaunched, setIsLaunched] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const hasPlayed = useRef(false);
  let t2;
  let t3;
  if ($[1] !== onArrive || $[2] !== onFinished || $[3] !== services) {
    t2 = () => {
      if (hasPlayed.current || services.length === 0) {
        return;
      }
      hasPlayed.current = true;
      const timers = [];
      let cancelScrollWatch = null;
      const cardFor = _temp;
      const announce = () => setAnnouncement(`${services.map(_temp2).join(", ")} added from your booking link.`);
      const finish = () => {
        announce();
        onArrive();
        onFinished();
      };
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const launch = () => {
        const counter = document.querySelector("[data-self-schedule-service-counter]");
        const target = counter ? centerOf(counter) : null;
        const measured = [];
        if (target && !prefersReducedMotion) {
          services.forEach(service_0 => {
            const card = cardFor(service_0.id);
            if (!card) {
              return;
            }
            const origin = onScreenCenterOf(card);
            if (!origin) {
              return;
            }
            measured.push({
              ...service_0,
              originX: origin.x,
              originY: origin.y,
              deltaX: target.x - origin.x,
              deltaY: target.y - origin.y,
              delay: measured.length * STAGGER_MS
            });
          });
        }
        if (measured.length === 0) {
          finish();
          return;
        }
        announce();
        setFlights(measured);
        requestAnimationFrame(() => requestAnimationFrame(() => setIsLaunched(true)));
        measured.forEach(flight => {
          timers.push(setTimeout(onArrive, flight.delay + FLIGHT_MS));
        });
        const lastFlight = measured[measured.length - 1];
        timers.push(setTimeout(() => {
          setFlights([]);
          onFinished();
        }, (lastFlight?.delay ?? 0) + FLIGHT_MS + 100));
      };
      const firstCard = services[0] ? cardFor(services[0].id) : null;
      if (firstCard && !isFullyVisible(firstCard)) {
        firstCard.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "center"
        });
        cancelScrollWatch = whenScrollSettles(firstCard, launch);
      } else {
        timers.push(setTimeout(launch, LAYOUT_SETTLE_MS));
      }
      return () => {
        timers.forEach(clearTimeout);
        cancelScrollWatch?.();
      };
    };
    t3 = [services, onArrive, onFinished];
    $[1] = onArrive;
    $[2] = onFinished;
    $[3] = services;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== flights || $[7] !== isLaunched) {
    let t5;
    if ($[9] !== isLaunched) {
      t5 = (flight_0, index) => <div key={`${flight_0.id}-${index}`} aria-hidden={true} className="fixed left-0 top-0 z-30 transition-transform ease-out will-change-transform" style={{
        transform: `translate3d(${isLaunched ? flight_0.originX + flight_0.deltaX : flight_0.originX}px, 0, 0)`,
        transitionDuration: `${FLIGHT_MS}ms`,
        transitionDelay: `${flight_0.delay}ms`
      }}><div className="transition-transform ease-in will-change-transform" style={{
          transform: `translate3d(0, ${isLaunched ? flight_0.originY + flight_0.deltaY : flight_0.originY}px, 0) scale(${isLaunched ? 0.55 : 1})`,
          transitionDuration: `${FLIGHT_MS}ms`,
          transitionDelay: `${flight_0.delay}ms`
        }}><div data-testid="preselected-service-chip" className={cx("-translate-x-1/2 -translate-y-1/2", "flex max-w-52 items-center gap-1.5 rounded-full", "bg-primary px-3 py-1.5 shadow-lg", "text-caption font-medium text-primary-foreground", "transition-opacity")} style={{
            opacity: isLaunched ? 0 : 1,
            transitionDuration: `${FLIGHT_MS * 0.35}ms`,
            transitionDelay: `${flight_0.delay + FLIGHT_MS * 0.65}ms`
          }}><FaCheck className="h-3 w-3 shrink-0" /><span className="truncate">{flight_0.name}</span></div></div></div>;
      $[9] = isLaunched;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = flights.map(t5);
    $[6] = flights;
    $[7] = isLaunched;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[11] !== announcement) {
    t5 = <p role="status" aria-live="polite" className="sr-only">{announcement}</p>;
    $[11] = announcement;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className="pointer-events-none">{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp2(service) {
  return service.name;
}
function _temp(serviceId) {
  return document.querySelector(`[data-service-card-id="${serviceId}"]`);
}
export default PreselectedServiceFlight;